import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette } from '@/config/theme';
import { useWeeklyChallenges } from '@/features/games/predictWeekly';
import type { PredictWeeklyChallenge } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** Icon per challenge event so each goal reads at a glance. */
function eventIcon(event: PredictWeeklyChallenge['event']): keyof typeof Ionicons.glyphMap {
  switch (event) {
    case 'pick_won':
      return 'checkmark-circle';
    case 'big_win':
      return 'flame';
    default:
      return 'flash';
  }
}

/** A single challenge row: icon + title, a slim progress bar, and the reward. */
function ChallengeRow({ challenge }: { challenge: PredictWeeklyChallenge }) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const { progress, target, completed } = challenge;
  const pct = target > 0 ? Math.min(1, progress / target) : 0;
  const tint = completed ? palette.win : palette.voltBright;

  return (
    <View className="gap-2 px-4 py-3">
      <View className="flex-row items-center gap-2.5">
        <View
          className="h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: tint + '22' }}
        >
          <Ionicons name={eventIcon(challenge.event)} size={15} color={tint} />
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
            {t(`predictWeekly.goals.${challenge.key}.title`, {
              count: challenge.target,
              target: formatNumber(challenge.target, numeralOptions),
            })}
          </AppText>
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {completed
              ? t('predictWeekly.doneRow', {
                  coins: formatNumber(challenge.reward_coins, numeralOptions),
                })
              : t('predictWeekly.progressRow', {
                  done: formatNumber(progress, numeralOptions),
                  target: formatNumber(target, numeralOptions),
                })}
          </AppText>
        </View>
        <View className="flex-row items-center gap-1">
          <Ionicons name="logo-bitcoin" size={13} color={palette.captain} />
          <AppText variant="caption" weight="bold" tabular style={{ color: palette.captain }}>
            {formatNumber(challenge.reward_coins, numeralOptions)}
          </AppText>
        </View>
      </View>

      {/* Progress bar. */}
      <View className="h-1.5 overflow-hidden rounded-full bg-inset">
        <View
          className="h-full rounded-full"
          style={{ width: `${Math.round(pct * 100)}%`, backgroundColor: tint }}
        />
      </View>
    </View>
  );
}

/**
 * "Weekly Challenges" — a rotating set of Predict & Win goals that pay bonus
 * coins (place N picks, get N correct, win an underdog). Progress auto-fills as
 * the user's picks settle. Self-hides until the backend is reachable / has a set.
 */
export function WeeklyChallenges({ className }: { className?: string }) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const query = useWeeklyChallenges();

  if (query.isPending) {
    return <Skeleton className={`mx-4 h-44 w-full ${className ?? ''}`} />;
  }

  const data = query.data;
  if (!data || data.challenges.length === 0) return null;

  return (
    <Card className={`mx-4 overflow-hidden p-0 ${className ?? ''}`}>
      {/* Header: title + this week's earned-coins tally. */}
      <View className="flex-row items-center gap-2.5 px-4 pb-1 pt-4">
        <View
          className="h-8 w-8 items-center justify-center rounded-full"
          style={{ backgroundColor: palette.voltGlass }}
        >
          <AppText variant="caption">🏆</AppText>
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
            {t('predictWeekly.title')}
          </AppText>
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {t('predictWeekly.subtitle', {
              done: formatNumber(data.completed_count, numeralOptions),
              total: formatNumber(data.total_count, numeralOptions),
            })}
          </AppText>
        </View>
        {data.coins_earned > 0 ? (
          <View className="flex-row items-center gap-1 rounded-full border border-line px-2 py-0.5">
            <Ionicons name="logo-bitcoin" size={12} color={palette.captain} />
            <AppText variant="micro" weight="bold" tabular style={{ color: palette.captain }}>
              {t('predictWeekly.earned', {
                coins: formatNumber(data.coins_earned, numeralOptions),
              })}
            </AppText>
          </View>
        ) : null}
      </View>

      {data.challenges.map((challenge, index) => (
        <View key={challenge.key} className={index > 0 ? 'border-t border-line' : ''}>
          <ChallengeRow challenge={challenge} />
        </View>
      ))}
    </Card>
  );
}
